import { inject, injectable } from "inversify";
import { computed, makeObservable, observable, action } from "mobx";
import type { IChatMessagesDataLayer } from "../types/IChatMessagesDataLayer";
import { ChatMessagesDataLayerSymbol } from "./ChatMessagesDataLayerSymbol";
import { fromPromise } from "mobx-utils";
import type { ComponentMessageType } from "../types/ComponentMessageType";
import get from "lodash/get";
import { updateDayjsGlobalLocale } from "../../commons/utils";
import { groupMessagesByLastMessage } from "../../commons/messagesUtils";
import dayjs, { Dayjs } from "dayjs";
import type { DateFilterMonthsOptions } from "@whatsllyorg/crm-components/ChatComponent/types/DateFilterMonthsOptions";
import { downloadBlob, downloadFileByUrl } from "@whatslly/ui-components/componentsUtils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { arrayToCsv } from "@whatslly/commons/csvUtils";

function formatDatetime(value: string | number): string {
  if (typeof value === "number" || (typeof value === "string" && !isNaN(Number(value)))) {
    return dayjs(Number(value)).format("L LTS");
  }
  return value;
}

export type DownloadFormat = "csv" | "pdf";
const EXPORT_HEADERS = [
  "Sender Name",
  "Sender Phone Number",
  "Receiver Name",
  "Receiver Phone Number",
  "Message Content",
  "Datetime",
  "File Name",
  "Is Edited",
  "Is Deleted",
  "ID",
];

export type { UIMessageType } from "../types/UIMessageType";
export type AvailableTabs = "personal" | "groups";
@injectable()
export class ChatMessagesStore {
  private _ticket = 1;
  selectedTab: AvailableTabs = "personal";
  selectedGroup?: string;
  private _groupTick = 0;
  private _showXMonthsAgo?: DateFilterMonthsOptions;

  dateFilterMode: "range" | "byMonth" = "byMonth";
  _dateRange?: { fromDate: Dayjs | null; toDate: Dayjs | null };

  constructor(@inject(ChatMessagesDataLayerSymbol) private readonly chatMessagesDataLayer: IChatMessagesDataLayer) {
    makeObservable<ChatMessagesStore, "_ticket" | "_groupTick" | "_getGroup" | "_showXMonthsAgo">(this, {
      _ticket: observable,
      selectedTab: observable,
      selectedGroup: observable,
      _groupTick: observable,
      _dateRange: observable.deep,
      dateRange: computed,
      setFilters: action,
      setSelectedGroup: action,
      refreshGroup: action,
      setSelectedTab: action,
      setDateFilterMode: action,
      setDateRange: action,
      refreshMessages: action.bound,
      _getGroup: observable,
      isDeleteAllowed: computed,
      messages: computed,
      messagesFiltered: computed,
      userId: computed,
      crm: computed,
      disableDeleteButton: computed,
      disableHeaderButton: computed,
      currentUserId: computed,
      currentEntities: computed,
      allowedToDelete: computed,
      allowSyncGroups: computed,
      group: computed,
      groups: computed,
      baseUrl: computed,
      _showXMonthsAgo: observable,
      setShowLastXMonthsTimeStamp: action,
      showXMonthsAgo: computed,
    });
  }

  get dateRange() {
    return (
      this._dateRange ?? {
        fromDate: dayjs().subtract(Number(this.chatMessagesDataLayer.getCustomDefaultDateFilterOption?.() ?? 3), "month"),
        toDate: dayjs(),
      }
    );
  }

  set dateRange(val: { fromDate: Dayjs | null; toDate: Dayjs | null }) {
    this._dateRange = val;
  }

  setDateFilterMode(mode: "range" | "byMonth") {
    this.dateFilterMode = mode;
  }

  setDateRange(dateRange: { fromDate: Dayjs | null; toDate: Dayjs | null }) {
    this.dateRange = {
      fromDate: dateRange.fromDate?.startOf("day") ?? null,
      toDate: dateRange.toDate?.endOf("day") ?? null,
    };
  }

  setSelectedGroup(val: string | undefined) {
    if (val === undefined) {
      this.chatMessagesDataLayer.onLeaveGroup?.();
    }
    this.selectedGroup = val;
  }

  setSelectedTab(val: AvailableTabs) {
    if (val === "personal") this.setSelectedGroup(undefined);
    this.selectedTab = val;
  }

  refreshMessages() {
    this._ticket += 1;
  }

  /**
   *
   * This function is used to force refresh by observing an observable instead of empty IF Statement with the observable as a dependency.
   * E.x: loadMessagesWithTick will force refresh when this._ticket/this.dateRange/this._showXMonthsAgo changes
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  watchToForceRefresh(observablesToRefresh: any[]) {}

  private loadMessagesWithTick() {
    this.watchToForceRefresh([this._ticket, this.dateRange, this._showXMonthsAgo]);
    if (this.dateFilterMode === "range") {
      return this.chatMessagesDataLayer.loadMessagesByRange(this.dateRange).then((messages) => groupMessagesByLastMessage(messages));
    }
    return this.chatMessagesDataLayer
      .loadMessages(this._showXMonthsAgo || this.chatMessagesDataLayer.getCustomDefaultDateFilterOption?.() || "3")
      .then((messages) => groupMessagesByLastMessage(messages));
  }

  async deleteMessages(msgIds: string[]) {
    await this.chatMessagesDataLayer.deleteMessages?.(msgIds);
  }
  downloadFile(url: string) {
    return new Promise((resolve) => {
      const filename = url.substring(url.lastIndexOf("/") + 1);
      downloadFileByUrl(filename, url);
      setTimeout(() => {
        resolve(true);
      }, 5000);
    });
  }
  /* istanbul ignore next */
  async downloadPdfFormat(msgIds: string[]) {
    try {
      const doc = new jsPDF({
        orientation: "landscape",
      });
      const msgs = (this.messages.value as ComponentMessageType[]).filter((msg) => msgIds.includes(msg.id));
      const pdfBody = [];
      for (const msg of msgs) {
        if (msg.file) {
          await this.downloadFile(msg.file.url);
        }
        const versions = msg.editedMessages?.length ? msg.editedMessages : [msg];
        for (const version of versions) {
          const versionDatetime = version.latestUpdate ?? version.datetime;
          pdfBody.push([
            version.senderName,
            version.senderPhoneNumber,
            version.receiverName!,
            version.receiverPhoneNumber!,
            version.msg,
            formatDatetime(versionDatetime),
            version.file?.title ?? "",
            version.isEdited ? "True" : "False",
            version.isDeleted ? "True" : "False",
            msg.id,
          ]);
        }
      }
      autoTable(doc, {
        head: [EXPORT_HEADERS],
        body: pdfBody,
      });
      doc.save(`messages-${dayjs().format("YYYY-MM-DD-HH-mm-ss")}.pdf`);
      // eslint-disable-next-line no-empty
    } catch (err) {}
  }

  /* istanbul ignore next */
  async downloadCsvFormat(msgIds: string[]) {
    try {
      const msgs = (this.messages.value as ComponentMessageType[]).filter((msg) => msgIds.includes(msg.id));
      const msgsContent = [EXPORT_HEADERS];
      for (const msg of msgs) {
        if (msg.file) {
          await this.downloadFile(msg.file.url);
        }
        const versions = msg.editedMessages?.length ? msg.editedMessages : [msg];
        for (const version of versions) {
          const versionDatetime = version.latestUpdate ?? version.datetime;
          msgsContent.push([
            version.senderName,
            version.senderPhoneNumber,
            version.receiverName!,
            version.receiverPhoneNumber!,
            version.msg,
            formatDatetime(versionDatetime),
            version.file?.title ?? "",
            version.isEdited ? "True" : "False",
            version.isDeleted ? "True" : "False",
            msg.id,
          ]);
        }
      }
      downloadBlob(arrayToCsv(msgsContent), `messages-${dayjs().format("YYYY-MM-DD-HH-mm-ss")}.csv`, "text/csv;charset=utf-8;");
      // eslint-disable-next-line no-empty
    } catch (err) {}
  }

  async downloadMessages(msgIds: string[], format: DownloadFormat) {
    if (format === "csv") {
      await this.downloadCsvFormat(msgIds);
      return;
    }
    await this.downloadPdfFormat(msgIds);
  }
  getEntityUrl(type: string, id: string) {
    return this.chatMessagesDataLayer.getEntityUrl(type, id);
  }

  get messages() {
    return fromPromise(this.loadMessagesWithTick());
  }

  setFilters(val: Record<string, string[]>) {
    this.chatMessagesDataLayer.filters = val;
  }

  getFilters() {
    return this.chatMessagesDataLayer.filters;
  }

  get showXMonthsAgo() {
    return this._showXMonthsAgo || this.chatMessagesDataLayer.getCustomDefaultDateFilterOption?.() || "3";
  }

  setShowLastXMonthsTimeStamp(numberOfMonthsToShow: DateFilterMonthsOptions) {
    this._showXMonthsAgo = numberOfMonthsToShow;
  }

  get messagesFiltered() {
    const filters = this.chatMessagesDataLayer.filters;
    const searchKeys = Object.keys(filters);
    return fromPromise(
      Promise.all([
        this.messages.then(
          (items) =>
            this.chatMessagesDataLayer.handleFilterMessages?.(items) ||
            items.filter((item) => !searchKeys.some((key) => filters[key].includes(get(item, key))))
        ),
        this.chatMessagesDataLayer.getUserLocale?.(),
      ]).then(([data, userLocale]) => {
        updateDayjsGlobalLocale(userLocale);
        return data;
      })
    );
  }

  get userId() {
    return fromPromise(this.chatMessagesDataLayer.getUserId());
  }

  get isDeleteAllowed() {
    return fromPromise(this.chatMessagesDataLayer.getIsDeleteAllowed?.() ?? Promise.resolve(true));
  }

  get crm() {
    return this.chatMessagesDataLayer.crm;
  }

  get baseUrl() {
    return this.chatMessagesDataLayer.baseUrl;
  }

  get disableDeleteButton() {
    return !(this.messages.state === "fulfilled" && this.messages.value.length > 0);
  }

  get disableHeaderButton() {
    return this.messages.state === "pending";
  }

  get currentUserId() {
    return this.userId.state === "fulfilled" ? this.userId.value : undefined;
  }

  get currentEntities() {
    if (this.messages.state !== "fulfilled") return [];
    return this.messages.value ?? [];
  }

  get allowedToDelete() {
    return this.isDeleteAllowed.state === "fulfilled" && this.isDeleteAllowed.value;
  }

  get groups() {
    return fromPromise(this.chatMessagesDataLayer.getGroups());
  }

  get allowSyncGroups() {
    return this.groups.state === "fulfilled" ? this.groups.value.allowSyncGroups : false;
  }

  private async _getGroup() {
    if (this.messagesFiltered.state === "pending") await this.messagesFiltered;
    return this.chatMessagesDataLayer.getGroup(this._showXMonthsAgo ?? "3", this.selectedGroup, this.dateRange, this.dateFilterMode).then((res) => {
      if (!res?.group) {
        return { group: undefined };
      }
      return { group: { ...res.group, messages: groupMessagesByLastMessage(res.group.messages) } };
    });
  }

  get group() {
    if (this._groupTick) {
      // force refresh
    }

    return fromPromise(this._getGroup());
  }

  refreshGroup() {
    this._groupTick += 1;
  }
}
